import { useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { observer } from 'mobx-react'

import { useStore } from 'Stores/index'

import { useAuthToken } from 'Hooks'

import type { userRolesEnum } from 'Constants/userRoles'

import { limitedRolesPermissionsConfig } from '../config/permissions.config'
import { AuthorisedLayout } from '../layouts'
import { GLOBAL_ROUTE_PATHS } from './Router.constants'
import type { ProtectedRouteProps } from './Router.types'

export const ProtectedRoute = observer((props: ProtectedRouteProps) => {
  const { component: Component, onRender, isAllowed, path } = props
  const store = useStore()
  const authToken = useAuthToken()
  const routeHash = window.location.hash === '#/' ? null : window.location.hash

  if (!authToken) {
    return (
      <Navigate
        to={{
          pathname: GLOBAL_ROUTE_PATHS.public.login,
          search: routeHash
            ? `?redirect-to=${encodeURIComponent(routeHash)}`
            : '',
        }}
      />
    )
  }

  useEffect(() => {
    if (onRender) {
      onRender()
    }
  }, [onRender])

  if (
    store.userStore.user.roles.some(role =>
      Object.keys(limitedRolesPermissionsConfig).includes(role),
    )
  ) {
    // Find first matching limited role config, there should be only one
    // If multiple limited roles are assigned to an user, the first found will be used
    const limitedRole = store.userStore.user.roles.find(role =>
      Object.keys(limitedRolesPermissionsConfig).includes(role),
    ) as userRolesEnum

    if (
      !limitedRolesPermissionsConfig[limitedRole]?.allowedPaths.includes(path)
    ) {
      return <Navigate to="/" />
    }
  }

  if (typeof isAllowed === 'function' && !isAllowed(store)) {
    return <Navigate to="/" />
  }

  const Layout = (
    <AuthorisedLayout>
      <Component />
    </AuthorisedLayout>
  )

  return Layout
})
